import {
  effect,
  inject,
  Injector,
  makeEnvironmentProviders,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import { environment } from '@env';
import { AuthService } from '@core/auth/auth.service';
import { CompanySessionService } from '@core/auth/company-session.service';
import { HotjarService } from './hotjar.service';

/** Installs window.hj + _hjSettings and injects the Hotjar loader script for the given site id. Idempotent. */
export function loadHotjar(siteId: string): void {
  if (typeof window === 'undefined' || typeof window.hj === 'function') return;

  window._hjSettings = { hjid: Number(siteId), hjsv: 6 };
  window.hj = function hj(...args: unknown[]) {
    (hj as unknown as { q: unknown[] }).q = (hj as unknown as { q?: unknown[] }).q ?? [];
    (hj as unknown as { q: unknown[] }).q.push(args);
  };

  const script = document.createElement('script');
  script.async = true;
  script.dataset['hotjar'] = '';
  script.src = `https://static.hotjar.com/c/hotjar-${encodeURIComponent(siteId)}.js?sv=6`;
  document.head.appendChild(script);
}

export interface HotjarDeps {
  hotjar: HotjarService;
  auth: AuthService;
  companySession: CompanySessionService;
  injector: Injector;
}

/** Loads Hotjar and identifies the current user/company via an effect. */
export function initHotjar(siteId: string, deps: HotjarDeps): void {
  if (!siteId) return;

  loadHotjar(siteId);

  effect(
    () => {
      const employer = deps.auth.employer();
      if (!employer) return;

      const company = deps.companySession.activeCompany();
      const role = deps.companySession.activeMembership()?.role;
      const attributes: Record<string, string> = company && role ? { company_id: company.id, role: String(role) } : {};
      deps.hotjar.identify(employer.id, attributes);
    },
    { injector: deps.injector },
  );
}

/** Provider entry point — add `provideHotjar()` to the app.config.ts providers array. */
export function provideHotjar(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      initHotjar(environment.hotjarSiteId, {
        hotjar: inject(HotjarService),
        auth: inject(AuthService),
        companySession: inject(CompanySessionService),
        injector: inject(Injector),
      });
    }),
  ]);
}
